// Kabukicho south (Oni turf, the chop yard, the avenue to the station line): building kinds and a roof prop that are
// specific to this region. Everything else in the scene reuses the kinds from hd-fronts-kabuki.ts.
import { K, px, disc, frame, shadow, dk, lt, alpha, bands, speckle, text, type G } from './hd-kit';
import { registerFront, registerRoofProp, type LightFn } from './hd-registry';
import { door, lantern, bandSign, fitS, plant } from './hd-parts';

/** a barred window: dark glass, a warm slit of light, iron bars */
function barred(g: G, x: number, y: number, w: number, h: number, glow: string, L: LightFn) {
  px(g, x - 2, y - 2, w + 4, h + 4, K.ink);
  bands(g, x, y, w, h, dk(glow, 0.55), dk(glow, 0.8), 4);
  px(g, x, y, w, 2, alpha(K.night, 0.5));
  for (let i = 5; i < w - 2; i += 8) px(g, x + i, y, 2, h, K.metalLo);
  px(g, x, y + Math.floor(h / 2), w, 2, K.metalLo);
  px(g, x - 3, y + h + 2, w + 6, 3, K.concreteLo); px(g, x - 3, y + h + 2, w + 6, 1, K.concreteHi); // sill
  L(x, y, w, h, glow, 0.3, true);
}

// Oni Syndicate frontage: blood-dark wall, heavy door under a noren, barred windows, red lanterns, a pink ONI band.
registerFront('kabukiSouthOni', { wall: dk(K.pink, 0.8), wallAc: true }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  px(g, x, gy - 10, w, 6, K.redDk); px(g, x, gy - 10, w, 1, K.red); px(g, x, gy - 4, w, 3, alpha(K.night, 0.5)); // lintel
  for (let i = 14; i < w - 12; i += 44) lantern(g, x + i, gy - 4, K.red, L);
  if (sign) bandSign(g, x + w / 2, y + 14, sign.text, sign.color, L, fitS(sign.text, w));
  const dw = 56, dx = x + Math.floor((w - dw) / 2);
  door(g, dx, gy + 6, dw, gf - 8, K.pink, L);
  for (let i = 0; i < 3; i++) { px(g, dx + 4 + i * 16, gy + 6, 15, 16, K.redDk); px(g, dx + 4 + i * 16, gy + 6, 15, 2, K.red); } // noren
  disc(g, dx + dw / 2, gy + 14, 4, 4, K.white);
  barred(g, x + 14, gy + 10, dx - x - 34, gf - 22, K.pink, L);
  barred(g, dx + dw + 20, gy + 10, x + w - (dx + dw) - 34, gf - 22, K.pink, L);
  plant(g, dx - 14, gy + gf - 20);
});

// the Rust Lotus chop shop garage: corrugated roller door half up on a lit bay, tyres, hazard kickplate
registerFront('kabukiSouthGarage', { wall: K.wallLo, upper: 'none' }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  px(g, x, y + 10, w, 6, K.metalLo); px(g, x, y + 10, w, 1, K.metalHi);
  for (let i = 0; i < w; i += 8) px(g, x + i, y + 10, 1, 6, K.ink);
  if (sign) bandSign(g, x + w / 2, y + 22, sign.text, sign.color, L, fitS(sign.text, w));
  const bx = x + 10, bw = w - 20, by = gy + 2, bh = gf - 4;
  px(g, bx - 2, by - 2, bw + 4, bh + 2, K.ink);
  bands(g, bx, by, bw, bh, dk(K.amber, 0.45), dk(K.amber, 0.8), 4); // lit bay
  px(g, bx + 6, by + bh - 14, 26, 3, K.metalHi); px(g, bx + 10, by + bh - 11, 4, 11, K.metalLo); px(g, bx + 24, by + bh - 11, 4, 11, K.metalLo); // car lift
  for (let k = 0; k < 2; k++) { disc(g, bx + bw - 14, by + bh - 6 - k * 8, 7, 4, K.ink); disc(g, bx + bw - 14, by + bh - 7 - k * 8, 5, 3, K.metalLo); } // tyres
  const sh = Math.floor(bh * 0.45); // the shutter, rolled up partway
  px(g, bx, by, bw, sh, K.metal);
  for (let j = 0; j < sh; j += 3) px(g, bx, by + j, bw, 1, j % 6 ? K.metalLo : K.metalHi);
  px(g, bx, by + sh, bw, 3, K.ink);
  px(g, bx + Math.floor(bw / 2) - 2, by + sh + 1, 4, 3, K.metalHi); // pull handle
  L(bx, by + sh, bw, bh - sh, K.amber, 0.45, true);
  for (let i = 0; i < w; i += 8) px(g, x + i, gy + gf - 3, 4, 3, K.amber); // hazard stripe
});

// the back wall of the chop yard, seen from inside it: roller doors, number plates, a caged work light
registerFront('kabukiSouthYard', { wall: K.wallLo, upper: 'none' }, ({ g, x, y, w, h, gf, gy, L }) => {
  speckle(g, x, y, w, h, K.concreteLo, 0.06, 41);
  const n = 3, dw = Math.floor((w - 24) / n);
  for (let k = 0; k < n; k++) {
    const dx = x + 12 + k * dw;
    px(g, dx, gy - 2, dw - 8, gf + 2, K.ink);
    px(g, dx + 2, gy, dw - 12, gf - 2, K.metal);
    for (let j = 0; j < gf - 2; j += 3) px(g, dx + 2, gy + j, dw - 12, 1, j % 6 ? K.metalLo : K.metalHi);
    px(g, dx + 2, gy, dw - 12, 2, alpha(K.night, 0.5));
    px(g, dx + Math.floor(dw / 2) - 4, gy + gf - 7, 8, 3, K.warmHi);
  }
  px(g, x + 4, gy - 6, w - 8, 3, K.amber); L(x + 4, gy - 6, w - 8, 3, K.amber, 0.45, true);
  text(g, 'ONI', x + 8, y + 3, K.redLo, 1);
});

// rooftop neon rack with a lit pink ONI board
registerRoofProp('kabukiSouthOniSign', ({ g, x, y, L }) => {
  shadow(g, x + 6, y + 14, 48, 28);
  px(g, x + 6, y + 22, 3, 16, K.metalLo); px(g, x + 40, y + 22, 3, 16, K.metalLo);
  px(g, x, y, 50, 26, K.ink); px(g, x + 2, y + 2, 46, 22, dk(K.pink, 0.7));
  frame(g, x + 3, y + 3, 44, 20, K.pink);
  text(g, 'ONI', x + 10, y + 7, lt(K.pink, 0.6), 3);
  L(x, y, 50, 26, K.pink, 0.55, true, true);
});
